import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSuburb } from "../App";
import { useAuth } from "../App";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Search,
  MapPin,
  ShoppingCart,
  TrendingDown,
  Truck,
  Shield,
  Clock,
  CheckCircle,
  Crown,
  Store,
  ArrowRight,
  ArrowDown,
  Sparkles,
  Loader2,
  CreditCard,
  DollarSign,
  Package,
  Heart,
  Quote,
} from "lucide-react";

const LandingPage = ({ openAuthModal }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectSuburb, suburb } = useSuburb();
  const { isAuthenticated, isMember, token } = useAuth();
  const [suburbInput, setSuburbInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNotAvailable, setShowNotAvailable] = useState(false);
  const [notAvailableMessage, setNotAvailableMessage] = useState("");
  const searchRef = useRef(null);

  // Check for auth query parameter to open signup modal
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const authParam = params.get("auth");
    if (authParam === "signup" && openAuthModal) {
      openAuthModal("register");
      // Clean up the URL
      navigate("/", { replace: true });
    }
  }, [location.search, openAuthModal, navigate]);
  
  // Suburb disambiguation state
  const [showSuburbChoice, setShowSuburbChoice] = useState(false);
  const [suburbOptions, setSuburbOptions] = useState([]);

  // Membership dialog state
  const [showMemberDialog, setShowMemberDialog] = useState(false);
  const [memberLoading, setMemberLoading] = useState(false);

  const scrollToSearch = () => {
    searchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Focus the input after scrolling
    setTimeout(() => {
      const input = searchRef.current?.querySelector('input');
      input?.focus();
    }, 500);
  };

  const handleBecomeMember = async () => {
    if (!isAuthenticated) {
      openAuthModal("register");
      return;
    }
    if (isMember) {
      toast.info("You're already a member!");
      return;
    }
    setMemberLoading(true);
    try {
      const response = await axios.post(`${API}/membership/subscribe`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setShowMemberDialog(true);
        setMemberLoading(false);
        window.__membershipEway = { access_code: response.data.access_code, form_action_url: response.data.form_action_url };
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start membership signup.");
      setMemberLoading(false);
    }
  };

  const submitMembershipPayment = (cardData) => {
    const eway = window.__membershipEway;
    if (!eway) return;
    const form = document.createElement("form");
    form.method = "POST";
    form.action = eway.form_action_url;
    form.style.display = "none";
    const fields = {
      EWAY_ACCESSCODE: eway.access_code,
      EWAY_PAYMENTTYPE: "Credit Card",
      EWAY_CARDNAME: cardData.name,
      EWAY_CARDNUMBER: cardData.number.replace(/\s/g, ""),
      EWAY_CARDEXPIRYMONTH: cardData.expiry_month.padStart(2, "0"),
      EWAY_CARDEXPIRYYEAR: cardData.expiry_year,
      EWAY_CARDCVN: cardData.cvn,
    };
    Object.entries(fields).forEach(([n, v]) => {
      const input = document.createElement("input");
      input.type = "hidden"; input.name = n; input.value = v;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  };

  // Fetch suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (suburbInput.length < 3) {
        setSuggestions([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/suburbs/suggestions?query=${suburbInput}`);
        setSuggestions(response.data.suggestions);
      } catch (error) {
        console.error("Error fetching suggestions:", error);
      }
    };
    const debounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounce);
  }, [suburbInput]);

  const handleSuburbSelect = async (suburbName, postcode = null) => {
    setLoading(true);
    try {
      const result = await selectSuburb(suburbName, postcode);
      
      if (result.needs_disambiguation) {
        setSuburbOptions(result.options);
        setShowSuburbChoice(true);
        setLoading(false);
        return;
      }
      
      if (result.coming_soon) {
        setNotAvailableMessage(result.message);
        setShowNotAvailable(true);
        setLoading(false);
        return;
      }
      
      if (result.valid) {
        toast.success(`Welcome! Shopping in ${result.suburb}, ${result.state} ${result.postcode}`);
        navigate(`/products?postcode=${result.postcode}`);
      } else {
        setNotAvailableMessage(result.message);
        setShowNotAvailable(true);
      }
    } catch (error) {
      toast.error("Failed to set location");
    } finally {
      setLoading(false);
      setSuburbInput("");
      setSuggestions([]);
    }
  };
  
  const handleSuburbChoiceSelect = async (option) => {
    setShowSuburbChoice(false);
    setLoading(true);
    try {
      const result = await selectSuburb(option.suburb, option.postcode);
      
      if (result.coming_soon) {
        setNotAvailableMessage(result.message);
        setShowNotAvailable(true);
        setLoading(false);
        return;
      }
      
      if (result.valid) {
        toast.success(`Welcome! Shopping in ${result.suburb}, ${result.state} ${result.postcode}`);
        navigate(`/products?postcode=${result.postcode}`);
      } else {
        setNotAvailableMessage(result.message || "Unable to find stores in this area");
        setShowNotAvailable(true);
      }
    } catch (error) {
      toast.error("Failed to set location");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (suburbInput.trim()) {
      handleSuburbSelect(suburbInput.trim());
    }
  };

  return (
    <div className="min-h-screen font-['Inter',sans-serif]">
      {/* Hero Section with Search */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="hero-section">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="container-main relative z-10 py-12">
          <div className="max-w-4xl mx-auto text-center">
            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 tracking-tight">
              Stop overpaying for<br />
              <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-sky-400 bg-clip-text text-transparent">
                your weekly shop.
              </span>
            </h1>

            <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Compare prices across retail stores. 
              One cart, one checkout, one delivery.
            </p>

            {/* Search Bar - Above the Fold */}
            <div ref={searchRef} className="max-w-xl mx-auto mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                <h2 className="text-lg font-semibold text-white mb-3">
                  Find the best prices near you
                </h2>
                <form onSubmit={handleSubmit} className="relative">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="text"
                        placeholder="Enter your suburb..."
                        className="pl-11 h-12 text-base bg-white border-0 rounded-xl focus:ring-2 focus:ring-emerald-500"
                        value={suburbInput}
                        onChange={(e) => setSuburbInput(e.target.value)}
                        data-testid="suburb-input"
                      />
                      
                      {/* Suggestions Dropdown */}
                      {suggestions.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
                          {suggestions.map((s, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => handleSuburbSelect(s.suburb, s.postcode)}
                              className="w-full px-4 py-3 text-left hover:bg-emerald-50 flex items-center gap-3 border-b last:border-b-0 transition-colors"
                              data-testid={`suggestion-${i}`}
                            >
                              <MapPin className="w-4 h-4 text-slate-400" />
                              <div>
                                <span className="font-medium text-slate-900">{s.suburb}</span>
                                <span className="text-slate-500 text-sm ml-2">
                                  {s.postcode} {s.lga && `(${s.lga})`}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      type="submit"
                      className="h-12 px-6 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white font-semibold rounded-xl shadow-lg"
                      disabled={loading}
                      data-testid="search-btn"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                    </Button>
                  </div>
                </form>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-6 text-slate-400 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>Free to use</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span>Secure payments</span>
              </div>
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-emerald-400" />
                <span>Same-week delivery</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The BundleCart Difference Section */}
      <section className="py-24 bg-white" data-testid="why-us-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Why Us</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-4">
              The BundleCart Difference
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto text-lg">
              Shopping smarter shouldn't be complicated.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Price Comparison */}
            <div className="text-center p-8 rounded-3xl bg-slate-50 hover:bg-emerald-50 transition-colors group">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/20 group-hover:scale-110 transition-transform">
                <TrendingDown className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Price Comparison</h3>
              <p className="text-slate-600 leading-relaxed">
                We compare prices across retail stores so you always get the best deal.
              </p>
            </div>

            {/* Flat-Fee Delivery */}
            <div className="text-center p-8 rounded-3xl bg-slate-50 hover:bg-sky-50 transition-colors group">
              <div className="w-20 h-20 bg-gradient-to-br from-sky-500 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-sky-500/20 group-hover:scale-110 transition-transform">
                <Truck className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Flat-Fee Delivery</h3>
              <p className="text-slate-600 leading-relaxed">
                One delivery fee, no matter how many stores. Bundle your order, we'll handle the rest.
              </p>
            </div>

            {/* Local Sydney Focus */}
            <div className="text-center p-8 rounded-3xl bg-slate-50 hover:bg-amber-50 transition-colors group">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-amber-500/20 group-hover:scale-110 transition-transform">
                <MapPin className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Local Sydney Focus</h3>
              <p className="text-slate-600 leading-relaxed">
                Built for Sydney. We know your local stores and deliver right to your door.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-white" data-testid="how-it-works-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="text-sky-600 font-semibold text-sm uppercase tracking-wider">Simple Process</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-4">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-4xl mx-auto">
            {[
              {
                step: "1",
                icon: <MapPin className="w-6 h-6" />,
                title: "Enter Your Suburb",
                description: "We'll find stores within 10km of you and show real-time prices",
              },
              {
                step: "2",
                icon: <ShoppingCart className="w-6 h-6" />,
                title: "Build Your Cart",
                description: "Add products from any store - we show you the best prices automatically",
              },
              {
                step: "3",
                icon: <Package className="w-6 h-6" />,
                title: "Checkout & Relax",
                description: "Pay once, schedule delivery - we shop and deliver it all",
              },
            ].map((item, index) => (
              <div key={index} className="text-center relative">
                <div className="w-20 h-20 bg-slate-900 rounded-2xl flex items-center justify-center text-white mx-auto mb-6 relative">
                  {item.icon}
                  <span className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-sm font-bold">
                    {item.step}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
                {index < 2 && (
                  <ArrowRight className="hidden md:block absolute top-10 -right-6 w-6 h-6 text-slate-300" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder's Note Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-cyan-50" data-testid="founders-note-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-lg mb-8">
              <Heart className="w-8 h-8 text-emerald-500" />
            </div>
            
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
              A Message from the Founders
            </h3>
            
            <div className="relative">
              <Quote className="absolute -top-4 -left-4 w-12 h-12 text-emerald-200 opacity-50" />
              <p className="text-xl md:text-2xl text-slate-700 leading-relaxed font-light italic px-8">
                We built this to simplify grocery shopping for our community. 
                One bundle, one price, zero stress.
              </p>
              <Quote className="absolute -bottom-4 -right-4 w-12 h-12 text-emerald-200 opacity-50 rotate-180" />
            </div>
            
            <div className="mt-8 flex items-center justify-center gap-3">
              <div className="flex -space-x-3">
                <div className="w-10 h-10 bg-slate-300 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold text-slate-600">S</div>
                <div className="w-10 h-10 bg-slate-300 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold text-slate-600">V</div>
                <div className="w-10 h-10 bg-slate-300 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold text-slate-600">J</div>
              </div>
              <p className="text-slate-600 text-sm">
                — The BundleCart Team, Sydney
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-slate-900" data-testid="cta-section">
        <div className="container-main text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Saving?
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Join your neighbours in Sydney who are already shopping smarter.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!isAuthenticated && (
              <Button
                className="h-12 px-8 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white font-semibold rounded-xl"
                onClick={() => openAuthModal("register")}
                data-testid="cta-signup-btn"
              >
                <Crown className="w-5 h-5 mr-2" />
                Sign Up & Become a Member
              </Button>
            )}
            {isAuthenticated && !isMember && (
              <Button
                className="h-12 px-8 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white font-semibold rounded-xl"
                onClick={handleBecomeMember}
                disabled={memberLoading}
                data-testid="cta-member-btn"
              >
                {memberLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Crown className="w-5 h-5 mr-2" />}
                Become a Member - $5/mo
              </Button>
            )}
            <Button
              variant="outline"
              className="h-12 px-8 border-white/20 text-white hover:bg-white/10 rounded-xl"
              onClick={scrollToSearch}
              data-testid="cta-guest-btn"
            >
              Shop as Guest
            </Button>
          </div>
        </div>
      </section>

      {/* Dialogs */}
      <Dialog open={showNotAvailable} onOpenChange={setShowNotAvailable}>
        <DialogContent data-testid="not-available-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-emerald-500" />
              Coming Soon to Your Area!
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-slate-600 py-4">
            {notAvailableMessage}
          </DialogDescription>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowNotAvailable(false)}
            >
              Try Another Suburb
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showSuburbChoice} onOpenChange={setShowSuburbChoice}>
        <DialogContent data-testid="suburb-choice-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-emerald-500" />
              Which suburb did you mean?
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              We found multiple suburbs with this name. Please select your location:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {suburbOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => handleSuburbChoiceSelect(option)}
                className="w-full p-4 text-left rounded-xl border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all flex items-center gap-3 group"
                data-testid={`suburb-option-${index}`}
              >
                <div className="w-10 h-10 bg-slate-100 group-hover:bg-emerald-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-slate-500 group-hover:text-emerald-600" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">{option.suburb}</div>
                  <div className="text-sm text-slate-500">
                    {option.postcode} {option.lga && `• ${option.lga}`}
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-emerald-600" />
              </button>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowSuburbChoice(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <MembershipCardDialog
        open={showMemberDialog}
        onClose={() => setShowMemberDialog(false)}
        onSubmit={submitMembershipPayment}
      />
    </div>
  );
};

const MembershipCardDialog = ({ open, onClose, onSubmit }) => {
  const [cardData, setCardData] = useState({ name: "", number: "", expiry_month: "", expiry_year: "", cvn: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(cardData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="membership-card-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-emerald-600" />
            Join the BundleCart Community
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Join for $5/month. Get 4% convenience fee instead of 7% on all your grocery bundles!
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <label className="text-sm font-medium text-slate-700">Name on Card</label>
            <Input placeholder="Card holder name" value={cardData.name} onChange={(e) => setCardData({ ...cardData, name: e.target.value })} required data-testid="mem-card-name" className="mt-1" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Card Number</label>
            <Input placeholder="4444 3333 2222 1111" value={cardData.number} onChange={(e) => setCardData({ ...cardData, number: e.target.value })} required data-testid="mem-card-number" className="mt-1" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Month</label>
              <Input placeholder="12" maxLength={2} value={cardData.expiry_month} onChange={(e) => setCardData({ ...cardData, expiry_month: e.target.value })} required data-testid="mem-card-month" className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Year</label>
              <Input placeholder="28" maxLength={2} value={cardData.expiry_year} onChange={(e) => setCardData({ ...cardData, expiry_year: e.target.value })} required data-testid="mem-card-year" className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">CVN</label>
              <Input placeholder="123" maxLength={3} type="tel" inputMode="numeric" value={cardData.cvn} onChange={(e) => setCardData({ ...cardData, cvn: e.target.value.replace(/\D/g, '').slice(0, 3) })} required data-testid="mem-card-cvn" className="mt-1" />
            </div>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3 text-sm text-emerald-800 flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            <span>Card details go directly to eWay - never stored on our servers</span>
          </div>
          <Button type="submit" className="w-full h-11 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white font-semibold" data-testid="mem-pay-btn">
            Pay $5.00 & Become a Member
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LandingPage;
